import React, { useEffect, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import FormControl from '../Form/FormControl';
import TextArea from '../Base/TextArea/TextArea';
import SubmitButton from '../Button/SubmitButton';
import Checkbox from '../Base/Checkbox/Checkbox';
import * as yup from 'yup';
import Select, { SelectValue } from '../Base/Select/Select';
import { useEffectAsync } from '../../helpers';
import { FormValues } from '../../Modules/MagicForm/Interfaces/FormValues';
import useTermItemSelect from '../../Modules/Term/hooks/useTermItemSelect';
import useTranslation from '../../Hooks/useTranslation';
import Loading from '../Loading';
import useWorkplaceRole from '../../Modules/Workplace/Hooks/useWorkplaceRole';
import { PaginationOrderHow } from '../../graphql/graphql';
import { DekurzFormValues } from '../Dekurz/Dekurz';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormWrapper from '../Form/FormWrapper';
import FormMultiplierRemoveButton from '../Form/FormMultiplierRemoveButton';
import RemoveButton from '../Button/RemoveButton';
import FormMultiplier from '../Form/FormMultiplier';
import DiagnosisSelectField from '../Base/Diagnosis/DiagnosisSelectField';
import { remapDiagnosisValues } from '../../Modules/MedicalReport/Services/medicalReport.service';
import usePatientConsent from '../PatientProfile/Hooks/usePatientConsent';
import DiscourseTextLoadButton from '../../Modules/Discourse/Components/DiscourseTextLoadButton/DiscourseTextLoadButton';
import PatientNoConsentProvided from '../../Modules/Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import InputWrapper from '../Form/components/InputWrapper/InputWrapper';
import TextAreaField from '../Form/components/TextAreaField';

interface MedicalReportDetailMouFormProps {
	onSubmit: (values: FormValues) => void;
	defaultData?: any;
	workplaceId: string;
	patientId: string;
}

const MedicalReportDetailMouForm: React.FC<MedicalReportDetailMouFormProps> = ({
	onSubmit,
	defaultData,
	workplaceId,
	patientId,
}) => {
	const { isConsentProvided } = usePatientConsent(patientId);
	const { t } = useTranslation();
	const [mouWorkplaceList, setMouWorkplaceList] = useState<SelectValue[]>([]);
	const [initialized, setInitialized] = useState<boolean>(false);
	const {
		userData: { id: userId },
	} = useAppContext();
	const { termItemSelectOptions } = useTermItemSelect({
		termType: 'mou_workplaces',
		useValue: false,
		take: 999,
		order: { key: 'rank', how: PaginationOrderHow.Asc },
		//filter: { value2: workplaceId },
	});
	const { termItemSelectOptions: termItemFallbackSelectOptions } =
		useTermItemSelect({ termType: 'mou_workplaces', useValue: false });
	const { isDoctor } = useWorkplaceRole({ userId, workplaceId });

	const handleSubmit = async (values: FormValues) => {
		values = prepareValues(values);
		values.type = 'outpatientMedicalReport';
		values['title'] = t('base.ambulanceReport');
		onSubmit(values);
	};

	const handleSubmitNote = async (values: FormValues) => {
		values = prepareValues(values);
		values.type = 'medicalReport';
		values['title'] = t('base.shared.note');
		onSubmit(values);
	};

	const prepareValues = (values: FormValues) => {
		if (typeof values.sendToPatient === 'undefined') {
			values.sendToPatient = false;
		}
		values.diagnoses = remapDiagnosisValues(values?.diagnoses);
		values.mouWorkplace = values.mouWorkplace?.value;
		return values;
	};

	const validationSchema = yup.object({
		mouWorkplace: yup.object().shape({
			value: yup
				.string()
				.uuid()
				.required(t('component.medicalReport.mouWorkplaces')),
		}),
		text: yup
			.string()
			.required(t('component.medicalReport.yourMessage'))
			.default(undefined),
	});

	const formMethods = useForm({
		defaultValues: defaultData,
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		formMethods.reset(defaultData);
	}, [defaultData]);

	useEffectAsync(async () => {
		const tmpWorkplaceList = await termItemSelectOptions();
		if (tmpWorkplaceList.length) {
			setMouWorkplaceList(tmpWorkplaceList);
		} else {
			const fallbackWorkplaceList = await termItemFallbackSelectOptions();
			setMouWorkplaceList(fallbackWorkplaceList);
		}
		setInitialized(true);
	});

	if (!initialized) {
		return (
			<div className='py-5'>
				<Loading />
			</div>
		);
	}

	return (
		<FormWrapper formMethods={formMethods}>
			<FormControl
				label={t('component.medicalReport.mouWorkplaces')}
				name='mouWorkplace'
				isRequired={true}>
				<Select
					placeholder={t('base.shared.pick')}
					options={mouWorkplaceList}
				/>
			</FormControl>
			<FormMultiplier
				name='diagnoses'
				addButtonTitle='base.addAnother'
				createDefault={1}>
				<DiagnosisSelectField
					name='diagnose'
					label='component.medicalReportCzechDefault.diagnosis'
				/>
				<FormMultiplierRemoveButton>
					<RemoveButton onClick={() => void 0} title='base.shared.remove' />
				</FormMultiplierRemoveButton>
			</FormMultiplier>
			<InputWrapper>
				<TextAreaField
					name='text'
					label='component.medicalReport.yourMessage'
					charactersLimit={3200}
					showCharCounter
					isRequired
				/>
			</InputWrapper>
			<DiscourseTextLoadButton
				patientId={patientId}
				workplaceId={workplaceId}
				className='mb-3'
			/>
			{isConsentProvided ? (
				<FormControl name='sendToPatient' hideRequired>
					<Checkbox tx={'component.medicalReport.sendToPatient'} />
				</FormControl>
			) : (
				<PatientNoConsentProvided patientId={patientId} />
			)}
			<div className='d-flex flex-row mt-4' style={{ gap: '15px' }}>
				<SubmitButton
					title={t('component.medicalReport.createNote')}
					onSubmit={handleSubmitNote}
				/>
				{isDoctor && (
					<SubmitButton
						title={t('component.medicalReport.createAmbulantReport')}
						onSubmit={handleSubmit}
					/>
				)}
			</div>
		</FormWrapper>
	);
};

export default MedicalReportDetailMouForm;
